// A tiny CLIENT-SIDE generator that runs entirely on the phone CPU (zero server
// cost). It is NOT a neural net: it is a minimal order-2 word Markov model,
// trained at runtime on the loaded intel/FACS corpus, in the Karpathy "makemore"
// spirit (smallest thing that generates). It composes short SHERLOCK-style
// readout lines locally. For real neural generation, WebLLM / transformers.js on
// WebGPU is the drop-in upgrade (see notes in the repo).

type Bigram = string; // "wordA wordB"

const model = new Map<Bigram, string[]>();
const starts: Bigram[] = [];
let trained = false;

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s'-]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 0);
}

/** Train the Markov model on a corpus of short texts. Idempotent-ish (accumulates). */
export function trainNano(texts: string[]): void {
  for (const t of texts) {
    const w = tokenize(t);
    if (w.length < 3) continue;
    starts.push(`${w[0]} ${w[1]}`);
    for (let i = 0; i + 2 < w.length; i++) {
      const key = `${w[i]} ${w[i + 1]}`;
      const next = w[i + 2]!;
      const arr = model.get(key);
      if (arr) arr.push(next);
      else model.set(key, [next]);
    }
  }
  trained = model.size > 0;
}

// Deterministic PRNG (no Math.random, which is banned): a simple LCG seeded per line.
function lcg(seed: number): () => number {
  let x = (seed * 2654435761 + 12345) & 0x7fffffff;
  return () => {
    x = (x * 1103515245 + 12345) & 0x7fffffff;
    return x / 0x7fffffff;
  };
}

/** Generate one short readout line deterministically from a seed index. */
export function genLine(seed: number, maxWords = 11): string {
  if (!trained || starts.length === 0) return "";
  const rnd = lcg(seed + 1);
  let key = starts[Math.floor(rnd() * starts.length) % starts.length]!;
  const words = key.split(" ");
  for (let i = 0; i < maxWords - 2; i++) {
    const nexts = model.get(key);
    if (!nexts || nexts.length === 0) break;
    const nw = nexts[Math.floor(rnd() * nexts.length) % nexts.length]!;
    words.push(nw);
    key = `${words[words.length - 2]} ${nw}`;
  }
  const line = words.join(" ");
  return line.charAt(0).toUpperCase() + line.slice(1);
}

export function nanoReady(): boolean {
  return trained;
}
